import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { useModal } from '../../../context/ModalContext';
import { changesApi } from '../../../services/changesApi';
import type { ChangeDetailTabDefinition, ChangeDetailTabFieldType } from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';
import { FORM_EDITABLE_CONFIGURABLE_STATUSES, STATUS_LABELS } from '../changeLabels';

const FIELD_TYPES: { value: ChangeDetailTabFieldType; label: string }[] = [
  { value: 'text_short', label: 'Texto curto' },
  { value: 'text_long', label: 'Texto longo' },
  { value: 'integer', label: 'Número inteiro' },
  { value: 'list', label: 'Lista' },
  { value: 'yes_no', label: 'Sim/Não' },
  { value: 'user_picker', label: 'Usuário (busca)' },
];

interface Props {
  tabId: number;
  onBack: () => void;
  onSaved: () => void;
}

const ChangeDetailTabBuilder: React.FC<Props> = ({ tabId, onBack, onSaved }) => {
  const { showConfirm } = useModal();
  const [tab, setTab] = useState<ChangeDetailTabDefinition | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [fieldLabel, setFieldLabel] = useState('');
  const [fieldType, setFieldType] = useState<ChangeDetailTabFieldType>('text_short');
  const [fieldRequired, setFieldRequired] = useState(false);
  const [fieldOptions, setFieldOptions] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await changesApi.getDetailTab(tabId);
      setTab(data);
      setError(null);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao carregar aba.'));
    }
  }, [tabId]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveTab = async () => {
    if (!tab) return;
    const ok = await showConfirm({
      title: 'Salvar aba',
      message: `Tem certeza que deseja salvar as alterações da aba «${tab.label}»?`,
      confirmLabel: 'Salvar',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    setSaving(true);
    try {
      await changesApi.updateDetailTab(tab.id, {
        label: tab.label,
        slug: tab.slug,
        is_visible: tab.is_visible,
        visible_statuses: tab.visible_statuses,
        enable_create: tab.enable_create,
        enable_pagination: tab.enable_pagination,
        enable_search: tab.enable_search,
      });
      onSaved();
      setError(null);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar a aba.'));
    } finally {
      setSaving(false);
    }
  };

  const addField = async () => {
    if (!tab || !fieldLabel.trim()) return;
    setSaving(true);
    try {
      const options =
        fieldType === 'list'
          ? fieldOptions
              .split('\n')
              .map((line) => line.trim())
              .filter(Boolean)
          : [];
      await changesApi.createDetailTabField(tab.id, {
        label: fieldLabel.trim(),
        field_type: fieldType,
        is_required: fieldRequired,
        options,
      });
      setFieldLabel('');
      setFieldOptions('');
      setFieldRequired(false);
      await load();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível criar o campo.'));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = (status: string) => {
    if (!tab) return;
    const set = new Set(tab.visible_statuses || []);
    if (set.has(status)) set.delete(status);
    else set.add(status);
    setTab({ ...tab, visible_statuses: Array.from(set) });
  };

  if (!tab) {
    return <p className="text-muted">Carregando aba…</p>;
  }

  return (
    <div>
      <Button variant="link" className="ps-0 mb-2" onClick={onBack}>
        ← Voltar para a lista
      </Button>
      {error ? (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Card className="mb-3 border">
        <Card.Header className="fw-semibold">1. Dados da aba</Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Nome na ficha</Form.Label>
                <Form.Control
                  value={tab.label}
                  onChange={(e) => setTab({ ...tab, label: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Identificador (slug)</Form.Label>
                <Form.Control
                  value={tab.slug}
                  onChange={(e) => setTab({ ...tab, slug: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Check
                type="switch"
                id="detail-tab-visible"
                label="Aba visível globalmente"
                checked={tab.is_visible}
                onChange={(e) => setTab({ ...tab, is_visible: e.target.checked })}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3 border">
        <Card.Header className="fw-semibold">2. Status em que a aba aparece</Card.Header>
        <Card.Body>
          <Row className="g-2">
            {FORM_EDITABLE_CONFIGURABLE_STATUSES.map((status) => (
              <Col md={4} key={status}>
                <Form.Check
                  type="checkbox"
                  id={`detail-tab-status-${status}`}
                  label={STATUS_LABELS[status] || status}
                  checked={(tab.visible_statuses || []).includes(status)}
                  onChange={() => toggleStatus(status)}
                />
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3 border">
        <Card.Header className="fw-semibold">3. Comportamento na ficha</Card.Header>
        <Card.Body>
          <Form.Check
            className="mb-2"
            type="switch"
            id="detail-tab-create"
            label="Botão Novo"
            checked={tab.enable_create}
            onChange={(e) => setTab({ ...tab, enable_create: e.target.checked })}
          />
          <Form.Check
            className="mb-2"
            type="switch"
            id="detail-tab-pagination"
            label="Paginação"
            checked={tab.enable_pagination}
            onChange={(e) => setTab({ ...tab, enable_pagination: e.target.checked })}
          />
          <Form.Check
            type="switch"
            id="detail-tab-search"
            label="Busca"
            checked={tab.enable_search}
            onChange={(e) => setTab({ ...tab, enable_search: e.target.checked })}
          />
        </Card.Body>
      </Card>

      <Card className="mb-3 border">
        <Card.Header className="fw-semibold">4. Campos</Card.Header>
        <Card.Body>
          <Table responsive size="sm" className="mb-3">
            <thead>
              <tr>
                <th>Campo</th>
                <th>Tipo</th>
                <th>Obrigatório</th>
              </tr>
            </thead>
            <tbody>
              {(tab.fields || []).map((field) => (
                <tr key={field.id}>
                  <td>{field.label}</td>
                  <td>
                    <Badge bg="light" text="dark">
                      {field.field_type}
                    </Badge>
                  </td>
                  <td>{field.is_required ? 'Sim' : 'Não'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Row className="g-2 align-items-end">
            <Col md={4}>
              <Form.Label className="small mb-1">Novo campo</Form.Label>
              <Form.Control value={fieldLabel} onChange={(e) => setFieldLabel(e.target.value)} />
            </Col>
            <Col md={3}>
              <Form.Select value={fieldType} onChange={(e) => setFieldType(e.target.value as ChangeDetailTabFieldType)}>
                {FIELD_TYPES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Check
                type="checkbox"
                label="Obrigatório"
                checked={fieldRequired}
                onChange={(e) => setFieldRequired(e.target.checked)}
              />
            </Col>
            <Col md="auto">
              <Button size="sm" disabled={saving || !fieldLabel.trim()} onClick={() => void addField()}>
                Adicionar campo
              </Button>
            </Col>
          </Row>
          {fieldType === 'list' ? (
            <Form.Group className="mt-2">
              <Form.Label className="small">Opções (uma por linha)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={fieldOptions}
                onChange={(e) => setFieldOptions(e.target.value)}
              />
            </Form.Group>
          ) : null}
        </Card.Body>
      </Card>

      <Card className="mb-3 border bg-light">
        <Card.Body>
          <div className="fw-semibold mb-2">Pré-visualização</div>
          <p className="text-muted small mb-2">
            Na ficha: aba «{tab.label}» com tabela, busca e botão Novo conforme os switches acima.
          </p>
          <Button size="sm" variant="outline-primary" disabled>
            Novo
          </Button>
        </Card.Body>
      </Card>

      <Button onClick={() => void saveTab()} disabled={saving}>
        {saving ? 'Salvando…' : 'Salvar aba'}
      </Button>
    </div>
  );
};

export default ChangeDetailTabBuilder;
